// DatabaseSettingsViewRow.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './DatabaseSettingsViewRow.css';

// Icons
import Icon from 'svg/Icon';

/**
 * DatabaseSettingsViewRow
 *
 * Renders one draggable view row in the database settings Views tab.
 *
 * @param {object} view - View definition for the rendered row.
 * @param {string} label - Display label for the row.
 * @param {string} note - Secondary descriptive text for the row.
 * @param {string} iconKey - View type icon to render.
 * @param {boolean} isDragging - Whether this row is currently being dragged.
 * @param {boolean} isDropTarget - Whether this row is the active drop target.
 * @param {boolean} isSelected - Whether this row is the currently selected view.
 * @param {boolean} [locked=false] - When true, disables edit + reorder (system views).
 * @param {string} [badge] - Optional badge label (e.g. System).
 * @param {function} onClick - Handler for selecting the row.
 * @param {function} onDragEnd - Handler for drag end.
 * @param {function} onDragOver - Handler for drag over.
 * @param {function} onDragStart - Handler for drag start.
 * @param {function} onDrop - Handler for drop.
 * @param {function} onSettingsClick - Handler for opening view settings.
 *
 * @returns {ReactNode} The rendered view row.
 */
const DatabaseSettingsViewRow = React.memo(({
    view,
    label,
    note,
    iconKey,
    isDragging,
    isDropTarget,
    isSelected,
    locked = false,
    badge = '',
    onClick,
    onDragEnd,
    onDragOver,
    onDragStart,
    onDrop,
    onSettingsClick,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    // ----- RENDER ----- //

    return (
        <div
          className={`database-settings-view-row-container${isDragging ? ' dragging' : ''}${locked ? ' locked' : ''}`}
          style={{
            backgroundColor: isDropTarget ? theme.backgroundHover : theme.panel,
            borderColor: isDropTarget ? theme.foregroundMuted : theme.backgroundHover,
            cursor: locked ? 'default' : 'move',
            opacity: locked ? 0.92 : 1,
          }}
          draggable={!locked}
          title={String(view?.title || '').trim() || 'View'}
          onClick={onClick}
          onDragEnd={locked ? undefined : onDragEnd}
          onDragOver={locked ? undefined : onDragOver}
          onDragStart={locked ? undefined : onDragStart}
          onDrop={locked ? undefined : onDrop}
        >

            {/* MAIN */}
            <div className="database-settings-view-row-main">

                {/* ICON */}
                <div className="database-settings-view-row-icon">
                    <Icon
                      iconKey={iconKey}
                      width={18}
                      height={18}
                      color={theme.foregroundMuted}
                    />
                </div>

                {/* COPY */}
                <div className="database-settings-view-row-copy">

                    {/* LABEL */}
                    <div
                      className="database-settings-view-row-label"
                    >
                        {label}
                        {badge ? (
                            <span
                              className="database-settings-view-row-badge"
                              style={{ color: theme.foregroundMuted }}
                            >
                              {badge}
                            </span>
                        ) : null}
                    </div>

                    {/* DESCRIPTION */}
                    {note ? (
                        <div
                          className="database-settings-view-row-description"
                        >
                            {note}
                        </div>
                    ) : null}
                </div>
            </div>

            {/* ACTIONS */}
            <div className="database-settings-view-row-actions">

                {/* ACTION */}
                {!locked ? (
                    <button
                      className="database-settings-view-row-action"
                      title="Edit view"
                      type="button"
                      onClick={(event) => {
                            event.stopPropagation();
                            onSettingsClick?.();
                        }}
                    >
                        <Icon
                          iconKey="settings"
                          width={16}
                          height={16}
                          color={theme.foregroundMuted}
                        />
                    </button>
                ) : (
                    <div
                      className="database-settings-view-row-action"
                      title="System views are edited in Admin → Types"
                    >
                        <Icon
                          iconKey="shield"
                          width={16}
                          height={16}
                          color={theme.foregroundMuted}
                        />
                    </div>
                )}

                {/* DRAG HANDLE */}
                {!locked ? (
                    <div
                      className="database-settings-view-row-drag-handle"
                      title="Drag to reorder"
                    >
                        <Icon
                          iconKey="drag_indicator"
                          width={18}
                          height={18}
                          color={theme.foregroundMuted}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
});

// Dev Tools
DatabaseSettingsViewRow.displayName = 'DatabaseSettingsViewRow';

export default DatabaseSettingsViewRow;
